import type { WizardAnswers } from '@/context/WizardSessionContext';
import { hasWizardParcelSelection } from '@/lib/validation/parcel';
import { apiGet, apiPatch, apiPost } from './client';
import {
  answersFromApi,
  answersToApi,
  type ApiAnswers,
} from './session-mapper';

export const CONSENT_VERSION = '2026-05-27-v1';

export type ClientMeta = {
  locale?: string;
};

export type IntakeSession = {
  id: string;
  status: string;
  current_step: number;
  answers: Record<string, unknown>;
  client_meta?: ClientMeta;
  consent_at?: string | null;
  consent_version?: string | null;
};

export type CreateSessionBody = {
  client_meta?: ClientMeta;
};

export type PatchSessionStepsBody = {
  current_step: number;
  answers: ApiAnswers | Record<string, unknown>;
  status?: string;
};

export type PatchSessionConsentBody = {
  consent_version: string;
};

export type PatchSessionClientMetaBody = {
  client_meta: ClientMeta;
};

import type { ParcelSelectionMode, PlanParcelBinding } from './map';

export type PostSessionParcelBody = {
  migrash: string;
  gush?: string;
  helka?: string;
  lat: number;
  lng: number;
  settlement_name?: string;
  designation?: string;
  designation_code?: string;
  plan_primary?: string;
  plans_affecting?: PlanParcelBinding[];
  area_sqm_registered?: number;
  geometry?: { type: string; coordinates: unknown };
  selection_mode?: ParcelSelectionMode;
  gisnet_raw?: Record<string, unknown>;
};

export type ParcelSelection = {
  id: string;
  migrash: string;
  gush?: string;
  helka?: string;
  lat: number;
  lng: number;
  settlement_name?: string;
  designation?: string;
  area_sqm_registered?: number;
  selected_at: string;
};

/** Backend `ParcelSelectionResponse` (POST/GET parcel). */
type ParcelApiResponse = {
  id: string;
  session_id?: string;
  migrash: string;
  gush?: number | string | null;
  helka?: number | string | null;
  lat?: number;
  lng?: number;
  centroid?: { lat: number; lng: number };
  settlement_name?: string | null;
  designation?: string | null;
  area_sqm_registered?: number | null;
  created_at?: string;
  selected_at?: string;
};

type SessionApiResponse = IntakeSession & {
  answers_json?: Record<string, unknown>;
};

function normalizeParcelFromApi(raw: ParcelApiResponse): ParcelSelection {
  const lat = raw.lat ?? raw.centroid?.lat;
  const lng = raw.lng ?? raw.centroid?.lng;
  if (lat == null || lng == null) {
    throw new Error('Parcel response missing coordinates');
  }
  return {
    id: raw.id,
    migrash: raw.migrash,
    gush: raw.gush != null ? String(raw.gush) : undefined,
    helka: raw.helka != null ? String(raw.helka) : undefined,
    lat,
    lng,
    settlement_name: raw.settlement_name ?? undefined,
    designation: raw.designation ?? undefined,
    area_sqm_registered: raw.area_sqm_registered ?? undefined,
    selected_at: raw.selected_at ?? raw.created_at ?? new Date().toISOString(),
  };
}

function normalizeSession(raw: SessionApiResponse): IntakeSession {
  const answersRaw = raw.answers ?? raw.answers_json ?? {};
  return {
    ...raw,
    answers: answersFromApi(answersRaw),
  };
}

export function createSession(body: CreateSessionBody = {}): Promise<IntakeSession> {
  return apiPost<SessionApiResponse>('/api/sessions', body).then(normalizeSession);
}

export function patchSessionSteps(
  sessionId: string,
  body: PatchSessionStepsBody & { answers: ApiAnswers | Record<string, unknown> },
): Promise<IntakeSession> {
  const payload = {
    current_step: body.current_step,
    status: body.status,
    answers:
      'venture' in body.answers || 'contact' in body.answers
        ? (body.answers as ApiAnswers)
        : answersToApi(body.answers as Record<string, unknown>),
  };
  return apiPatch<SessionApiResponse>(`/api/sessions/${sessionId}/steps`, payload).then(
    normalizeSession,
  );
}

export function acceptConsent(
  sessionId: string,
  body: PatchSessionConsentBody,
): Promise<IntakeSession> {
  return apiPatch<SessionApiResponse>(`/api/sessions/${sessionId}/consent`, body).then(
    normalizeSession,
  );
}

/** Sync locale to server when user toggles language mid-wizard. */
export function patchSessionClientMeta(
  sessionId: string,
  body: PatchSessionClientMetaBody,
): Promise<IntakeSession> {
  return apiPatch<SessionApiResponse>(`/api/sessions/${sessionId}`, body).then(
    normalizeSession,
  );
}

export function getSessionParcel(sessionId: string): Promise<ParcelSelection> {
  return apiGet<ParcelApiResponse>(`/api/sessions/${sessionId}/parcel`).then(
    normalizeParcelFromApi,
  );
}

/** After consent: save the parcel already chosen in wizard step 1 (no second map pick). */
export async function persistWizardParcelSelection(
  sessionId: string,
  answers: WizardAnswers,
): Promise<ParcelSelection> {
  if (!hasWizardParcelSelection(answers)) {
    throw new Error('Wizard parcel selection is incomplete');
  }
  const migrash = answers.migrash!.trim();
  const lat = answers.map_lat!;
  const lng = answers.map_lng!;

  try {
    return await postSessionParcel(sessionId, {
      migrash,
      gush: answers.gush,
      helka: answers.helka,
      lat,
      lng,
      settlement_name: answers.settlement_name,
      designation: answers.designation,
      plan_primary: answers.plan_primary,
      area_sqm_registered: answers.area_sqm_registered,
      geometry: answers.parcel_geometry,
      selection_mode: answers.selection_mode,
    });
  } catch (postErr) {
    throw postErr;
  }
}

export function postSessionParcel(
  sessionId: string,
  body: PostSessionParcelBody,
): Promise<ParcelSelection> {
  return apiPost<
    ParcelApiResponse | { parcel_selection: ParcelApiResponse }
  >(`/api/sessions/${sessionId}/parcel`, body).then((raw) => {
    if (raw && typeof raw === 'object' && 'parcel_selection' in raw) {
      return normalizeParcelFromApi(raw.parcel_selection);
    }
    return normalizeParcelFromApi(raw as ParcelApiResponse);
  });
}
